import React, { useState } from 'react';
import { Button, Input, Card } from '../../shared/components';
import { userService } from '../../shared/services';

const ChildProfileScreen: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState('');
  const [childGender, setChildGender] = useState<'male' | 'female' | ''>('');
  const [childInterests, setChildInterests] = useState('');
  const [childFavoriteColor, setChildFavoriteColor] = useState('');
  const [childFavoriteAnimal, setChildFavoriteAnimal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!childName || !childAge || !childGender) {
      setError('Por favor completa el nombre, la edad y el género');
      return;
    }

    setLoading(true);

    try {
      // Guardar perfil del menor en localStorage
      const childProfile = {
        name: childName,
        age: childAge,
        gender: childGender,
        interests: childInterests,
        favoriteColor: childFavoriteColor,
        favoriteAnimal: childFavoriteAnimal,
        createdAt: new Date().toISOString()
      };

      localStorage.setItem('calmi_child_profile', JSON.stringify(childProfile));
      localStorage.setItem('calmi_child_profile_completed', 'true');

      setLoading(false);
      onComplete();
    } catch (err) {
      setError('Error al guardar el perfil. Por favor intenta de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4 sm:p-6 md:p-8 flex items-center justify-center">
      <div className="w-full max-w-md">
        <Card className="p-6 sm:p-8 bg-white/90 backdrop-blur-sm border border-purple-200 shadow-xl">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">👶</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
              Crea el perfil del menor
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Para que Calmi pueda conocer mejor al niño/a
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del niño/a *
              </label>
              <Input
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder="Ej: Sofía"
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Edad *
              </label>
              <Input
                type="number"
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
                placeholder="Ej: 8"
                required
                min="1"
                max="18"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Género *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setChildGender('male')}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    childGender === 'male'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">👦</span>
                  <span className="text-sm font-medium mt-1">Niño</span>
                </button>
                <button
                  type="button"
                  onClick={() => setChildGender('female')}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    childGender === 'female'
                      ? 'border-pink-500 bg-pink-50 text-pink-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">👧</span>
                  <span className="text-sm font-medium mt-1">Niña</span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ¿Qué le gusta hacer? (opcional)
              </label>
              <Input
                type="text"
                value={childInterests}
                onChange={(e) => setChildInterests(e.target.value)}
                placeholder="Ej: Dibujar, jugar fútbol, leer..."
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ¿Cuál es su color favorito? (opcional)
              </label>
              <Input
                type="text"
                value={childFavoriteColor}
                onChange={(e) => setChildFavoriteColor(e.target.value)}
                placeholder="Ej: Azul, rosa..."
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ¿Cuál es su animal favorito? (opcional)
              </label>
              <Input
                type="text"
                value={childFavoriteAnimal}
                onChange={(e) => setChildFavoriteAnimal(e.target.value)}
                placeholder="Ej: Perro, gato, conejo..."
                className="w-full"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-full shadow-md hover:shadow-lg transition-all"
            >
              {loading ? 'Guardando...' : 'Continuar →'}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            * Campos obligatorios
          </p>
        </Card>
      </div>
    </div>
  );
};

export default ChildProfileScreen;
